import { getErrorMessage } from "@airange/shared";
import {
  DrawerForm,
  ProFormCheckbox,
  ProFormDependency,
  ProFormRadio,
} from "@ant-design/pro-components";
import { Alert, App, Card, Descriptions, Form, Space, Tag, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ADMIN_HORIZONTAL_DIALOG_FORM_PROPS } from "./admin-form";
import { IdentityUserText } from "./identity-user-text";
import type {
  SharedResourceShareDrawerAdapter,
  SharedResourceShareDrawerRecord,
} from "./shared-resource-share-drawer-adapter";
import type {
  SharedResourceShareCapability,
  SharedResourceShareFormValues,
  SharedResourceShareMode,
  SharedResourceShareMutationPayload,
} from "./shared-resource-share-form-adapter";
import {
  fromSharedResourcePickerUser,
  mergeIdentityUserIntoSharedResourceShareUser,
  type SharedResourceShareUserIdentity,
  toSharedResourcePickerUser,
} from "./shared-resource-share-user-adapter";
import { SsoOrganizationUserPickerModal } from "./sso-organization-user-picker";
import type {
  SsoOrganizationDirectoryPickerBootstrap,
  SsoOrganizationDirectoryPickerUserSearchQuery,
  SsoOrganizationDirectoryPickerUserSearchResponse,
  SsoOrganizationPickerUser,
} from "./sso-organization-user-picker-types";
import { SsoSelectedUserPreviewTable } from "./sso-selected-user-preview-table";

type MessageValues = Record<string, string | number | boolean | null | undefined>;
type FormatMessage = (descriptor: { id: string }, values?: MessageValues) => string;

type SharedResourceShareDrawerProps<
  TRecord extends SharedResourceShareDrawerRecord<TCapability>,
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
  TPayload extends SharedResourceShareMutationPayload<
    TCapability,
    string | number
  > = SharedResourceShareMutationPayload<TCapability>,
  TResourceKey extends string | number = number,
> = {
  open: boolean;
  record: TRecord | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => Promise<void>;
  closePanel: (resource?: string) => void;
  adapter: SharedResourceShareDrawerAdapter<TRecord, TCapability, TPayload, TResourceKey>;
  formatMessage: FormatMessage;
  loadDirectoryPickerBootstrap: () => Promise<SsoOrganizationDirectoryPickerBootstrap>;
  searchDirectoryPickerUsers: (
    query: SsoOrganizationDirectoryPickerUserSearchQuery,
  ) => Promise<SsoOrganizationDirectoryPickerUserSearchResponse>;
  loadShareUsersByIds?: (userIds: number[]) => Promise<SharedResourceShareUserIdentity[]>;
};

export function SharedResourceShareDrawer<
  TRecord extends SharedResourceShareDrawerRecord<TCapability>,
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
  TPayload extends SharedResourceShareMutationPayload<
    TCapability,
    string | number
  > = SharedResourceShareMutationPayload<TCapability>,
  TResourceKey extends string | number = number,
>({
  open,
  record,
  onOpenChange,
  onSaved,
  closePanel,
  adapter,
  formatMessage,
  loadDirectoryPickerBootstrap,
  searchDirectoryPickerUsers,
  loadShareUsersByIds,
}: SharedResourceShareDrawerProps<TRecord, TCapability, TPayload, TResourceKey>) {
  const { message } = App.useApp();
  const [form] = Form.useForm<SharedResourceShareFormValues<TCapability>>();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerUsers, setPickerUsers] = useState<SsoOrganizationPickerUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<
    SharedResourceShareFormValues<TCapability>["shared_users"]
  >([]);
  const [submitting, setSubmitting] = useState(false);
  const {
    resourceKey,
    i18nPrefix,
    persistenceKeyPrefix,
    toFormValues,
    normalizePayload,
    getResourceKey,
    updateResource,
    getShareModeLabel,
    supportedCapabilities,
    identifierLabelKey,
    titleLabelKey,
    getIdentifierText,
    getTitleText,
  } = adapter;

  useEffect(() => {
    if (!open || !record) {
      form.resetFields();
      setSelectedUsers([]);
      setPickerUsers([]);
      return;
    }
    let cancelled = false;
    const initialValues = toFormValues(record);
    const initialUsers = initialValues.shared_users;
    void (async () => {
      let nextUsers = initialUsers;
      const userIds = Array.from(
        new Set(initialUsers.map((item) => item.user_id).filter((value) => value > 0)),
      );
      if (loadShareUsersByIds && userIds.length > 0) {
        try {
          const details = await loadShareUsersByIds(userIds);
          if (cancelled) {
            return;
          }
          const detailsById = new Map(details.map((item) => [item.id, item]));
          nextUsers = initialUsers.map((item) =>
            mergeIdentityUserIntoSharedResourceShareUser(item, detailsById.get(item.user_id)),
          );
        } catch (error) {
          if (cancelled) {
            return;
          }
          message.error(
            getErrorMessage(error, formatMessage({ id: "common.identityPicker.loadDataFailed" })),
          );
          return;
        }
      }
      setSelectedUsers(nextUsers);
      setPickerUsers(nextUsers.map((item) => toSharedResourcePickerUser(item)));
      form.setFieldValue("share_mode", initialValues.share_mode);
      form.setFieldValue("shared_users", nextUsers);
    })();
    return () => {
      cancelled = true;
    };
  }, [form, formatMessage, loadShareUsersByIds, message, open, record, toFormValues]);

  const handlePickerSubmit = useCallback(async () => {
    setSelectedUsers((previous) => {
      const previousById = new Map(previous.map((item) => [item.user_id, item]));
      const nextUsers = pickerUsers.map((item) =>
        fromSharedResourcePickerUser(item, previousById.get(item.id), supportedCapabilities),
      );
      form.setFieldValue("shared_users", nextUsers);
      return nextUsers;
    });
    setPickerOpen(false);
    return true;
  }, [form, pickerUsers, supportedCapabilities]);

  const pickerSelectedUsers = useMemo(
    () => selectedUsers.map((item) => toSharedResourcePickerUser(item)),
    [selectedUsers],
  );

  const labelPrefix = `${i18nPrefix}.share`;
  const columnPrefix = `${i18nPrefix}.columns`;
  const formPrefix = `${i18nPrefix}.forms`;
  const identifierColumnLabelId = identifierLabelKey ?? `${columnPrefix}.imageIdentifier`;
  const titleColumnLabelId = titleLabelKey ?? `${columnPrefix}.imageTitle`;
  const identifierText = record ? (getIdentifierText?.(record) ?? record.name) : "";
  const titleText = record ? (getTitleText?.(record) ?? record.display_name ?? record.name) : "";

  return (
    <>
      <DrawerForm<SharedResourceShareFormValues<TCapability>>
        form={form}
        title={formatMessage({ id: `${labelPrefix}.title` })}
        open={open}
        loading={submitting}
        onOpenChange={(nextOpen) => {
          onOpenChange(nextOpen);
          if (!nextOpen) {
            closePanel(resourceKey);
          }
        }}
        drawerProps={{ destroyOnHidden: true, size: "large" }}
        {...ADMIN_HORIZONTAL_DIALOG_FORM_PROPS}
        submitter={{
          submitButtonProps: {
            loading: submitting,
          },
          searchConfig: {
            submitText: formatMessage({ id: "common.actions.save" }),
          },
        }}
        onFinish={async (values) => {
          if (!record) {
            return false;
          }
          if (values.share_mode === "shared_to_users" && selectedUsers.length <= 0) {
            message.error(formatMessage({ id: `${labelPrefix}.errors.selectAtLeastOneUser` }));
            return false;
          }
          if (
            values.share_mode === "shared_to_users" &&
            selectedUsers.some((item) => item.capabilities.length <= 0)
          ) {
            message.error(formatMessage({ id: `${labelPrefix}.errors.selectCapabilities` }));
            return false;
          }
          setSubmitting(true);
          try {
            const payload = normalizePayload({
              share_mode: values.share_mode,
              shared_users: selectedUsers,
            });
            await updateResource(getResourceKey(record), payload);
            message.success(formatMessage({ id: `${labelPrefix}.messages.saveSuccess` }));
            await onSaved();
            onOpenChange(false);
            closePanel(resourceKey);
            return true;
          } catch (error) {
            message.error(
              getErrorMessage(error, formatMessage({ id: `${labelPrefix}.messages.saveFailed` })),
            );
            return false;
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {record ? (
          <Space orientation="vertical" size={16} style={{ display: "flex" }}>
            <Alert type="info" showIcon title={formatMessage({ id: `${labelPrefix}.notice` })} />
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label={formatMessage({ id: identifierColumnLabelId })}>
                {identifierText}
              </Descriptions.Item>
              <Descriptions.Item label={formatMessage({ id: titleColumnLabelId })}>
                {titleText}
              </Descriptions.Item>
              <Descriptions.Item label={formatMessage({ id: `${columnPrefix}.shareMode` })}>
                {getShareModeLabel(record.share_mode, formatMessage)}
              </Descriptions.Item>
            </Descriptions>
            <ProFormRadio.Group
              name="share_mode"
              label={formatMessage({ id: `${columnPrefix}.shareMode` })}
              options={[
                { label: getShareModeLabel("private", formatMessage), value: "private" },
                {
                  label: getShareModeLabel("shared_to_users", formatMessage),
                  value: "shared_to_users",
                },
                { label: getShareModeLabel("public", formatMessage), value: "public" },
              ]}
              rules={[
                {
                  required: true,
                  message: formatMessage({ id: `${formPrefix}.requiredShareMode` }),
                },
              ]}
            />
            <ProFormDependency name={["share_mode"]}>
              {({ share_mode }: { share_mode?: SharedResourceShareMode }) => {
                if (share_mode === "public") {
                  return (
                    <Card size="small">
                      <Space orientation="vertical" size={8}>
                        <Typography.Text>
                          {formatMessage({ id: `${labelPrefix}.publicDescription` })}
                        </Typography.Text>
                        <Space wrap>
                          {supportedCapabilities.map((capability) => (
                            <Tag key={capability} color="green">
                              {formatMessage({ id: `${labelPrefix}.capabilities.${capability}` })}
                            </Tag>
                          ))}
                        </Space>
                      </Space>
                    </Card>
                  );
                }
                if (share_mode !== "shared_to_users") {
                  return null;
                }
                return (
                  <Space orientation="vertical" size={12} style={{ display: "flex" }}>
                    <SsoSelectedUserPreviewTable
                      users={pickerSelectedUsers}
                      persistenceKey={`${persistenceKeyPrefix}-selected-users`}
                      title={formatMessage({ id: `${labelPrefix}.selectedUsersTitle` })}
                      emptyText={formatMessage({ id: `${labelPrefix}.emptyUsers` })}
                      pickMembersLabel={formatMessage({ id: `${labelPrefix}.actions.pickUsers` })}
                      onPickMembers={() => {
                        setPickerUsers(
                          selectedUsers.map((item) => toSharedResourcePickerUser(item)),
                        );
                        setPickerOpen(true);
                      }}
                      onRemoveUser={(user) => {
                        setSelectedUsers((previous) => {
                          const nextUsers = previous.filter((item) => item.user_id !== user.id);
                          setPickerUsers(nextUsers.map((item) => toSharedResourcePickerUser(item)));
                          form.setFieldValue("shared_users", nextUsers);
                          return nextUsers;
                        });
                      }}
                    />
                    <Space orientation="vertical" size={12} style={{ display: "flex" }}>
                      {selectedUsers.map((user, index) => (
                        <Card
                          key={user.user_id}
                          size="small"
                          title={
                            <IdentityUserText
                              userId={user.user_id}
                              username={user.username}
                              fullName={user.full_name}
                              displayText={user.full_name || user.username || String(user.user_id)}
                              detailLabels={{
                                userId: formatMessage({ id: "common.fields.userId" }),
                                username: formatMessage({ id: "common.fields.username" }),
                                fullName: formatMessage({ id: "common.fields.displayName" }),
                              }}
                            />
                          }
                        >
                          <ProFormCheckbox.Group
                            name={["shared_users", index, "capabilities"]}
                            label={formatMessage({ id: `${labelPrefix}.fields.capabilities` })}
                            options={supportedCapabilities.map((capability) => ({
                              label: formatMessage({
                                id: `${labelPrefix}.capabilities.${capability}`,
                              }),
                              value: capability,
                            }))}
                            rules={[
                              {
                                required: true,
                                message: formatMessage({
                                  id: `${labelPrefix}.errors.selectCapabilities`,
                                }),
                              },
                            ]}
                            fieldProps={{
                              onChange: (nextValues) => {
                                setSelectedUsers((previous) =>
                                  previous.map((item) =>
                                    item.user_id === user.user_id
                                      ? { ...item, capabilities: nextValues as TCapability[] }
                                      : item,
                                  ),
                                );
                              },
                            }}
                          />
                        </Card>
                      ))}
                    </Space>
                  </Space>
                );
              }}
            </ProFormDependency>
          </Space>
        ) : null}
      </DrawerForm>
      <SsoOrganizationUserPickerModal
        title={formatMessage({ id: `${labelPrefix}.actions.pickUsers` })}
        submitText={formatMessage({ id: "common.actions.confirm" })}
        cancelText={formatMessage({ id: "common.actions.cancel" })}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSubmit={handlePickerSubmit}
        persistenceKey={`${persistenceKeyPrefix}-user-picker`}
        selectedUsers={pickerUsers}
        onSelectedUsersChange={setPickerUsers}
        loadDirectoryPickerBootstrap={loadDirectoryPickerBootstrap}
        searchDirectoryPickerUsers={searchDirectoryPickerUsers}
      />
    </>
  );
}
